import { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { TrendingUp, ShoppingCart, Package, Users, Briefcase, MessageSquare, DollarSign } from "lucide-react";

const KPI = [
    { key: "revenue", label: "Revenue (paid)", icon: DollarSign, color: "text-secondary", format: money },
    { key: "orders_total", label: "Orders", icon: ShoppingCart, color: "text-primary" },
    { key: "customers", label: "Customers", icon: Users, color: "text-accent" },
    { key: "products", label: "Active products", icon: Package, color: "text-secondary" },
    { key: "applications", label: "Applications", icon: Briefcase, color: "text-primary" },
    { key: "new_messages", label: "New messages", icon: MessageSquare, color: "text-accent" },
];

export default function AdminDashboard() {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/admin/stats").then((r) => setData(r.data)).catch(() => {});
    }, []);

    if (!data) return <div className="p-8">Loading…</div>;

    return (
        <div className="p-8" data-testid="admin-dashboard">
            <h1 className="font-display text-3xl md:text-4xl font-bold mb-1">Dashboard</h1>
            <p className="text-sm text-muted-foreground mb-8">Snapshot of your business right now.</p>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
                {KPI.map((k) => (
                    <div key={k.key} className="bg-white border border-border p-5" data-testid={`kpi-${k.key}`}>
                        <k.icon className={`w-5 h-5 mb-3 ${k.color}`} />
                        <div className="font-display font-bold text-2xl tabular-nums">
                            {k.format ? k.format(data.kpis[k.key]) : (data.kpis[k.key] ?? 0).toLocaleString()}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">{k.label}</div>
                    </div>
                ))}
            </div>

            <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white border border-border" data-testid="recent-orders">
                    <div className="p-4 border-b border-border font-display font-bold">Recent orders</div>
                    <div className="divide-y divide-border">
                        {data.recent_orders.slice(0, 8).map((o) => (
                            <div key={o.id} className="px-4 py-3 flex items-center justify-between text-sm">
                                <div>
                                    <div className="font-mono font-bold text-xs">{o.order_number}</div>
                                    <div className="text-xs text-muted-foreground">{o.shipping_name}</div>
                                </div>
                                <div className="text-right">
                                    <div className="font-semibold">{money(o.total)}</div>
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{o.status}</div>
                                </div>
                            </div>
                        ))}
                        {data.recent_orders.length === 0 && <div className="px-4 py-6 text-sm text-muted-foreground text-center">No orders yet</div>}
                    </div>
                </div>

                <div className="bg-white border border-border" data-testid="top-products">
                    <div className="p-4 border-b border-border font-display font-bold flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-secondary" /> Top products
                    </div>
                    <div className="divide-y divide-border">
                        {data.top_products.map((p, i) => (
                            <div key={i} className="px-4 py-3 flex items-center justify-between text-sm">
                                <div>
                                    <div className="font-semibold">{p.name}</div>
                                </div>
                                <div className="font-mono">× {p.count}</div>
                            </div>
                        ))}
                        {data.top_products.length === 0 && <div className="px-4 py-6 text-sm text-muted-foreground text-center">No data yet</div>}
                    </div>
                </div>
            </div>
        </div>
    );
}
